import React, { useState } from "react";
import { ShieldCheck, ShieldAlert, Check, X, Loader2, Wand2, ChevronDown } from "lucide-react";
import { atsFix } from "@/lib/api";

/* ATS approval panel: deterministic checks a recruiter's ATS runs before a human
   reads the résumé. `ats` is the backend's score_ats() result. When `resumeText`
   and `onFixed` are given, a one-click wording fix (no new facts) is offered. */
export default function AtsPanel({ ats, targetRole, resumeText, onFixed, dark = false }) {
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(true);
  if (!ats) return null;
  const failed = ats.checks.filter((c) => !c.pass);
  const text = dark ? "text-stone-200" : "text-stone-700";
  const sub = dark ? "text-stone-400" : "text-stone-500";

  const fix = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await atsFix({ resume_text: resumeText, target_role: targetRole });
      setChanges({ list: data.changes, before: data.before, after: data.ats.score });
      onFixed && onFixed(data.resume_text, data.ats);
    } catch (e) { setErr(e?.response?.data?.detail || "Couldn't fix the wording. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border p-5 ${ats.approved ? (dark ? "border-emerald-400/30 bg-emerald-400/10" : "border-emerald-200 bg-emerald-50") : (dark ? "border-amber-300/30 bg-amber-300/10" : "border-amber-200 bg-amber-50")}`} data-testid="ats-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {ats.approved ? <ShieldCheck size={22} className="text-emerald-600" /> : <ShieldAlert size={22} className="text-amber-600" />}
          <div>
            <p className={`font-medium ${dark ? "text-stone-50" : "text-stone-900"}`} data-testid="ats-verdict">
              {ats.approved ? "ATS approved" : "Not ATS approved yet"} · {ats.score}/100
            </p>
            <p className={`text-xs ${sub}`}>
              {ats.approved ? "Passes the filters recruiters' software applies before a human reads it." : `${failed.length} check${failed.length > 1 ? "s" : ""} to fix. Approved at ${ats.approve_at}+ with every critical check passing.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!ats.approved && resumeText && onFixed && (
            <button onClick={fix} disabled={busy} data-testid="ats-fix" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}{busy ? "Fixing wording…" : "Fix wording for ATS"}
            </button>
          )}
          <button onClick={() => setOpen((o) => !o)} className={`text-xs inline-flex items-center gap-1 ${sub}`}>{open ? "Hide" : "Show"} checks <ChevronDown size={12} className={open ? "rotate-180" : ""} /></button>
        </div>
      </div>
      {changes && (
        <p className={`mt-3 text-xs ${text}`} data-testid="ats-changes">Wording fixed ({changes.before} → {changes.after}): {changes.list.join(" · ")}. No facts were added.</p>
      )}
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      {open && (
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {ats.checks.map((c) => (
            <li key={c.key} className="flex gap-2 text-sm" data-testid={`ats-check-${c.key}`}>
              {c.pass ? <Check size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <X size={15} className="text-red-500 mt-0.5 shrink-0" />}
              <span className={text}>
                {c.label}{c.critical && !c.pass && <span className="ml-1 text-[10px] font-mono uppercase text-red-500">critical</span>}
                {!c.pass && <span className={`block text-xs ${sub}`}>{c.fix}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && ats.missing_keywords?.length > 0 && (
        <p className={`mt-3 text-xs ${sub}`}>Keywords recruiters search for this role that aren't on your résumé (add only the ones you truly have): {ats.missing_keywords.slice(0, 10).join(", ")}.</p>
      )}
    </div>
  );
}
